import { animateButton, IAnimateButton } from './DexeroCore/animateButton';
import { bindings, IBindings } from './DexeroCore/bindings';
import { blockUI, IBlockUI } from './DexeroCore/blockUI';
import { defaultOptions, dexeroCoreDefaults, IDexeroCoreDefaults } from './DexeroCore/defaults';
import { doBlockUI, IDoBlockUI } from './DexeroCore/doBlockUI';
import { doUnblockUI, IDoUnblockUI } from './DexeroCore/doUnblockUI';
import { generateJasperReport, IGenerateJasperReport } from './DexeroCore/generateJasperReport';
import { highlightElm, IHighlightElm } from './DexeroCore/highlightElm';
import {
  IPositionLastPopedPopover,
  positionLastPopedPopover,
} from './DexeroCore/positionLastPopedPopover';
import { IResetLastPopedPopover, resetLastPopedPopover } from './DexeroCore/resetLastPopedPopover';
import { ISetLastPopedPopover, setLastPopedPopover } from './DexeroCore/setLastPopedPopover';
import { ISetMomentLang, setMomentLang } from './DexeroCore/setMomentLang';
import { IShowLockScreen, showLockScreen } from './DexeroCore/showLockScreen';
import { IUserToastMsg, userToastMsg } from './DexeroCore/userToastMsg';

export interface ICore {
  accentMap: IDCA.IGenericObj;
  ajaxInitArr: IDCA.IGenericFunc[];
  aliasCoreObject: IDCA.IGenericFunc;
  animateButton: IAnimateButton;
  baseURL: string;
  bindings: IBindings;
  blockUI: IBlockUI;
  browser: IDCA.IGenericObj;
  defaults: IDexeroCoreDefaults;
  defaultURL: string;
  detectBrowser: IDCA.IGenericFunc;
  doBlockUI: IDoBlockUI;
  doUnblockUI: IDoUnblockUI;
  generateJasperReport: IGenerateJasperReport;
  generateJasperReportNew: IGenerateJasperReport;
  getUserOptions: () => IDCA.IUserOptions;
  hideLockScreen: IDCA.IGenericFunc;
  highlightElm: IHighlightElm;
  init: (ops: IDCA.IUserOptions) => void;
  initAjaxDependents: IDCA.IGenericFunc;
  initAppOptions: (userOps: IDCA.IUserOptions) => void;
  initFunctionsArr?: IDCA.IGenericFunc[];
  langObj: { [key: string]: string };
  lastModalTrigger?: IjQueryElmOrHtmlElm;
  lastPopedPopover?: IjQueryElmOrHtmlElm;
  lastPopedPopoverContent?: IjQueryElmOrHtmlElm;
  lastPopedPopoverContentPosition?: string;
  lastPopedPopoverScrollTable?: IjQueryElmOrHtmlElm;
  mainInit: IDCA.IGenericFunc;
  o: JQuery<{}>;
  options: IDCA.IUserOptions;
  phoneMask: string;
  positionLastPopedPopover: IPositionLastPopedPopover;
  publish: (...args: any[]) => void;
  resetLastPopedPopover: IResetLastPopedPopover;
  setLastPopedPopover: ISetLastPopedPopover;
  setMomentLang: ISetMomentLang;
  showLockScreen: IShowLockScreen;
  showToastCloseButton: boolean;
  subscribe: (key: string, callbackFn: (...args: any[]) => any) => void;
  swaggerClient?: ISwagger;
  swaggerClientIsInitializing?: boolean;
  toastPosition: string;
  typingTimer: any;
  unblockUI: (target: IjQueryElmOrHtmlElm) => void;
  unsubscribe: (key: string, callbackFn: (...args: any[]) => any) => void;
  userOptions: IDCA.IUserOptions;
  userToastMsg: IUserToastMsg;
}
const DexeroCore: ICore = {
  accentMap: {},
  ajaxInitArr: [bindings],
  aliasCoreObject() {
    window.DCA = typeof window.DCA === 'undefined' ? (this as any) : $.extend({}, window.DCA, this);
  },
  animateButton,
  baseURL: '',
  bindings,
  blockUI,
  browser: {},
  defaultURL: '',
  defaults: dexeroCoreDefaults,
  detectBrowser() {
    const UAString = navigator.userAgent;
    let isIE10 = false;
    let isIE11 = false;

    if (navigator.appVersion.indexOf('MSIE 10') !== -1) {
      isIE10 = true;
    }
    if (UAString.indexOf('Trident') !== -1 && UAString.indexOf('rv:11') !== -1) {
      isIE11 = true;
    }
    /* Browser detection patch */
    (jQuery as any).browser = {};
    (jQuery as any).browser.mozilla =
      /mozilla/.test(navigator.userAgent.toLowerCase()) &&
      !/webkit/.test(navigator.userAgent.toLowerCase());
    (jQuery as any).browser.webkit = /webkit/.test(navigator.userAgent.toLowerCase());
    (jQuery as any).browser.opera = /opera/.test(navigator.userAgent.toLowerCase());
    (jQuery as any).browser.msie =
      /msie/.test(navigator.userAgent.toLowerCase()) || isIE10 || isIE11;
    this.browser = {};
    this.browser.mozilla =
      /mozilla/.test(navigator.userAgent.toLowerCase()) &&
      !/webkit/.test(navigator.userAgent.toLowerCase());
    this.browser.webkit = /webkit/.test(navigator.userAgent.toLowerCase());
    this.browser.opera = /opera/.test(navigator.userAgent.toLowerCase());
    this.browser.msie = /msie/.test(navigator.userAgent.toLowerCase()) || isIE10 || isIE11;
  },
  doBlockUI,
  doUnblockUI,
  generateJasperReport,
  generateJasperReportNew: generateJasperReport, // Deprecated
  getUserOptions() {
    return window.DexeroCoreApp.options;
  },
  hideLockScreen() {
    window.DexeroCoreApp.unblockUI($('body'));
  },
  highlightElm,
  init(ops: IDCA.IUserOptions) {
    this.aliasCoreObject();
    this.setMomentLang();
    this.detectBrowser();
    this.initAppOptions(ops);
    this.mainInit();
  },
  initAjaxDependents() {
    for (const func of window.DexeroCoreApp.ajaxInitArr) {
      try {
        func();
      } catch (e) {
        console.warn(e);
      }
    }
  },
  initAppOptions(userOps: IDCA.IUserOptions) {
    const dOps = this.defaults;
    const options = $.extend({}, dOps, userOps);
    this.defaultURL = options.defaultURL;
    this.baseURL = options.baseURL;
    this.showToastCloseButton = options.showToastCloseButton;
    this.toastPosition = options.toastPosition;
    this.phoneMask = options.phoneMask;
    this.langObj = options.langObj || {};
    this.accentMap = options.accentMap;
    this.userOptions = options;
    this.options = options;
  },
  initFunctionsArr: [bindings],
  langObj: {},
  lastModalTrigger: undefined,
  lastPopedPopover: undefined,
  lastPopedPopoverContent: undefined,
  lastPopedPopoverContentPosition: undefined,
  lastPopedPopoverScrollTable: undefined,
  mainInit() {
    window.toastr.options.closeButton = this.options.showToastCloseButton;
    window.toastr.options.positionClass = this.options.toastPosition;

    const funcs = this.initFunctionsArr;

    this.initFunctionsArr = undefined;
    if (funcs) {
      for (const func of funcs) {
        try {
          func();
        } catch (e) {
          console.warn(e);
        }
      }
    }
  },
  o: $({}),
  options: defaultOptions,
  phoneMask: '',
  positionLastPopedPopover,
  publish() {
    // @ts-ignore
    this.o.trigger(...arguments);
  },
  resetLastPopedPopover,
  setLastPopedPopover,
  setMomentLang,
  showLockScreen,
  showToastCloseButton: true,
  subscribe() {
    // @ts-ignore
    this.o.on(...arguments);
  },
  toastPosition: '',
  typingTimer: '',
  unblockUI(target: IjQueryElmOrHtmlElm) {
    window.DexeroCoreApp.doUnblockUI(target);
  },
  unsubscribe() {
    // @ts-ignore
    this.o.off(...arguments);
  },
  userOptions: defaultOptions,
  userToastMsg,

  // return {
  //   init(ops) {
  //     aliasCoreObject();
  //     setMomentLang();
  //     detectBrowser();
  //     initAppOptions(ops);
  //     mainInit();
  //   },
  //   subscribe,
  //   unsubscribe,
  //   publish,
  //   userToastMsg,
  //   blockUI,
  //   unblockUI,
  //   showLockScreen,
  //   hideLockScreen,
  //   highlightElm,
  //   animateButton,
  //   generateJasperReport,
  //   setLastPopedPopover,
  //   resetLastPopedPopover,
  //   positionLastPopedPopover,
  //   mainInit,
  //   initAjaxDependents,
  //   initFunctionsArr,
  //   ajaxInitArr,
  //   getUserOptions,
  //   userOptions: options,
  // };
};

export default DexeroCore;
